import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminContests, type Contest, contestTypeLabels, contestStatusColors, mockParticipants } from "@/mocks/adminContests";
import { radioStations } from "@/mocks/radios";

function formatDateSpanish(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

export default function TvContestDetailPage() {
  const navigate = useNavigate();
  const { contestId } = useParams<{ contestId: string }>();
  const [contest, setContest] = useState<Contest | null>(null);
  const [stationName, setStationName] = useState<string>("");
  const [focusedBtn, setFocusedBtn] = useState<string>("back");
  const [showQr, setShowQr] = useState(false);

  const buttons = ["back", "radio", "qr"] as const;

  useEffect(() => {
    if (!contestId) return;
    const numId = Number(contestId);
    if (isNaN(numId)) return;
    const c = adminContests.find((c) => c.id === numId);
    if (!c) return;
    setContest(c);
    const radio = radioStations.find((r) => r.id === c.stationId);
    if (radio) setStationName(radio.name);
  }, [contestId]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
        setFocusedBtn(buttons[(idx + 1) % buttons.length]);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
        setFocusedBtn(buttons[(idx - 1 + buttons.length) % buttons.length]);
      } else if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        if (showQr) { setShowQr(false); return; }
        navigate("/tv-app");
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (focusedBtn === "back") navigate("/tv-app");
        else if (focusedBtn === "radio" && contest) navigate(`/tv-app/radio/${contest.stationId}`);
        else if (focusedBtn === "qr") setShowQr(true);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [focusedBtn, navigate, contest, showQr]);

  if (!contest) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center select-none">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-white/5 flex items-center justify-center mb-6"><i className="ri-trophy-line text-white/40 text-3xl"></i></div>
          <h1 className="text-2xl font-bold text-white mb-3">Concurso no encontrado</h1>
          <button onClick={() => navigate("/tv-app")} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold cursor-pointer">Volver</button>
        </div>
      </div>
    );
  }

  const statusInfo = contestStatusColors[contest.status];
  const typeLabel = contestTypeLabels[contest.type];
  const topParticipants = mockParticipants.slice(0, 5);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white select-none overflow-x-hidden">
      <div className="relative h-[350px] lg:h-[440px] overflow-hidden bg-[#0a0a0f]">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-500/15 via-[#0a0a0f]/90 to-[#0a0a0f]"></div>
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 lg:px-10 py-5 z-10">
          <button onClick={() => navigate("/tv-app")} className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-semibold ${focusedBtn === "back" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30" : "bg-white/10 hover:bg-white/15 text-white"}`}>
            <i className="ri-arrow-left-line text-lg"></i>Volver
          </button>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${statusInfo.text} ${statusInfo.bg}`}>{contest.status === "activo" ? "Activo" : contest.status === "proximamente" ? "Próximo" : contest.status === "pausado" ? "Pausado" : "Finalizado"}</span>
            <span className="px-3 py-1.5 rounded-full bg-violet-500/90 text-white text-xs font-bold">{typeLabel}</span>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 z-10 px-6 lg:px-10 pb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-white">{contest.title}</h1>
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <span className="text-lg text-white/60"><i className="ri-calendar-line mr-1.5"></i>{contest.startDate} → {contest.endDate}</span>
            {stationName && <span className="text-lg text-white/60">{stationName}</span>}
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-10 py-8 space-y-8">
        {/* Prize Badge */}
        <div className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-gradient-to-r from-violet-500/10 to-amber-500/10 border border-violet-500/20">
          <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center"><i className="ri-gift-line text-violet-400 text-xl"></i></div>
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wide">Premio</p>
            <p className="text-xl font-bold text-white">{contest.prize}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-10 flex-wrap">
          <div><p className="text-3xl font-bold text-white">{formatCount(contest.participants)}</p><p className="text-sm text-white/50 mt-1">Participantes</p></div>
          <div className="w-px h-10 bg-white/10"></div>
          <div><p className="text-3xl font-bold text-accent-400">{formatCount(contest.shares)}</p><p className="text-sm text-white/50 mt-1">Compartidos</p></div>
        </div>

        {/* Description */}
        <div className="max-w-3xl">
          <p className="text-base text-white/60 leading-relaxed">{contest.description}</p>
        </div>

        {/* Rules */}
        <div className="max-w-3xl">
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2"><i className="ri-file-list-3-line text-violet-400"></i>Bases y condiciones</h3>
          <div className="p-5 rounded-2xl bg-white/5 border border-white/5">
            <p className="text-sm text-white/50 whitespace-pre-line leading-relaxed">{contest.rules}</p>
          </div>
        </div>

        {/* Winner */}
        {contest.winnerName && (
          <div className="max-w-md">
            <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2"><i className="ri-trophy-fill text-amber-400"></i>Ganador</h3>
            <div className="flex items-center gap-4 p-5 rounded-2xl bg-amber-500/10 border border-amber-500/20">
              {contest.winnerAvatar && <img src={contest.winnerAvatar} alt={contest.winnerName} className="w-14 h-14 rounded-full object-cover" />}
              <div>
                <p className="text-xl font-bold text-white">{contest.winnerName}</p>
                {contest.winnerPublished ? (
                  <p className="text-sm text-emerald-400 flex items-center gap-1"><i className="ri-check-line text-xs"></i>Resultado publicado</p>
                ) : (
                  <p className="text-sm text-amber-400 flex items-center gap-1"><i className="ri-time-line text-xs"></i>Pendiente de publicación</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Participants Preview */}
        <div>
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2"><i className="ri-group-line text-violet-400"></i>Últimos participantes</h3>
          <div className="flex items-center gap-3 flex-wrap">
            {topParticipants.map((p) => (
              <div key={p.id} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5">
                <img src={p.avatar} alt={p.name} className="w-8 h-8 rounded-full object-cover" />
                <span className="text-sm text-white/70">{p.name}</span>
              </div>
            ))}
            {contest.participants > 5 && <span className="text-sm text-white/30">+{contest.participants - 5} más</span>}
          </div>
        </div>

        <div className="flex items-center justify-center gap-3 pt-4 pb-8">
          <button onClick={() => navigate("/tv-app")} className={`flex items-center gap-2 px-5 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${focusedBtn === "back" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"}`}><i className="ri-arrow-left-line text-lg"></i>Volver</button>
          <button onClick={() => contest && navigate(`/tv-app/radio/${contest.stationId}`)} className={`flex items-center gap-2 px-5 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${focusedBtn === "radio" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"}`}><i className="ri-radio-line text-lg"></i>Ver emisora</button>
          <button onClick={() => setShowQr(true)} className={`flex items-center gap-2 px-5 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${focusedBtn === "qr" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"}`}><i className="ri-qr-code-line text-lg"></i>Participar desde el celu</button>
        </div>

        {showQr && (
          <div className="fixed inset-0 z-50 bg-[#0a0a0f]/90 backdrop-blur-sm flex items-center justify-center" onClick={() => setShowQr(false)}>
            <div className="bg-[#0f0f15] border border-white/10 rounded-3xl p-8 max-w-md w-full mx-4 text-center" onClick={(e) => e.stopPropagation()}>
              <div className="w-48 h-48 mx-auto rounded-2xl bg-white p-3 mb-6"><img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://radiofrecuenciaweb.com/events`)}`} alt="QR" className="w-full h-full object-contain rounded-xl" /></div>
              <h3 className="text-xl font-bold text-white mb-2">Participar desde el celular</h3>
              <p className="text-sm text-white/50 mb-2">Escaneá el QR para participar en este concurso desde tu celular.</p>
              <p className="text-sm text-white/30 font-mono mb-6">radiofrecuenciaweb.com/events</p>
              <button onClick={() => setShowQr(false)} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold cursor-pointer">Cerrar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}